/**
 * Turns a published band (from `shared`) into what the panel actually renders beside a metric: the
 * badge text, and — only where the band is entitled to one — a tone.
 *
 * Pure and DOM-free, so "when may a number be coloured" is unit-testable without a query client.
 *
 * Three rules downgrade a band, and each exists because following the design mock literally would
 * have shipped a claim we cannot support:
 *
 * 1. **A `reference` band never colours.** Meta's own funnel counts are directional for comparing one
 *    ad against another, not absolute rates. Stating "4–8% typical" beside a neutral figure is
 *    information; painting the figure red is a verdict the number cannot carry.
 * 2. **A `video` band is withheld from a non-video creative.** Every play-derived rate reads
 *    structurally lower on a static, so applying the band would mark a static weak for being a static.
 * 3. **A money band outside its own currency degrades to `reference`.** A CPM anchor quoted in USD is
 *    not a verdict on a EUR account; it is context, and it says so.
 */
import {
    CREATIVE_METRIC_BANDS,
    CROSS_SURFACE_STEP_THROUGH_KEYS,
    DIRECTIONAL_PURCHASES_FLOOR,
    MIN_IMPRESSIONS_FOR_RATE_VERDICT,
    VERDICT_PURCHASES_FLOOR,
    creativeBandEdge,
    type ICreativeBandKey,
    PURCHASE_CALIBRATED_BAND_KEYS,
    bandJudgesOutcome,
} from 'shared'
import type { IMetricTone } from '@/features/creative-performance/creative-performance-tone'

export interface IBandBadge {
    /** The band, stated in one line — lives in the tooltip, not beside the value. */
    badge: string
    /** `neutral` for every reference band; the edge's own tone for a verdict band. */
    tone: IMetricTone
    /**
     * One-line composition kept for native `title` attrs on card/table cells.
     * Prefer `reading` + `grounding` when building a structured hover.
     */
    reason: string
    /** Edge label when a value was placed (`weak` / `strong`). Null when withheld or no value. */
    reading: string | null
    /** Published doctrine / anchor copy — never the withhold preface. */
    grounding: string
    /** True when the verdict was withheld but the band range is still worth stating. */
    withheld: boolean
}

export interface IBandContext {
    /** Whether the creative carries video delivery. Gates every `video`-scoped band. */
    isVideo: boolean
    /**
     * Whether the ad's FORMAT is video — narrower than `isVideo`, which is true for any creative
     * carrying a video asset. Gates the two rates whose numerator and denominator are scoped to
     * different renditions (see `MIXED_RENDITION_KEYS`).
     */
    isVideoFormat: boolean
    /** The account currency the panel is rendering in, for money-denominated bands. */
    currency: string
    /** Delivery behind the play/impression-derived rates — the floor for a verdict on one. */
    impressions: number
    /**
     * Conversions behind the conversion-derived rates — the floor for a verdict on one.
     *
     * The account's TERMINAL event, not necessarily a purchase: the floor is about sample size, and
     * five trials support a ratio exactly as well as five purchases do.
     */
    purchases: number
    /**
     * Whether the account's terminal event is a purchase, and therefore whether the published bands
     * apply at all.
     *
     * `ECOM_ANCHORS.roasRatio` is a purchase-derived median. Judging a trial-value return against it
     * would grade one population with another's yardstick, so a non-purchase account gets the band
     * STATED and the verdict withheld — the same treatment a currency mismatch already gets.
     */
    isPurchaseOutcome?: boolean
}

/**
 * The play-derived rates that are only meaningful on a VIDEO-FORMAT ad.
 *
 * On a CAROUSEL / COLLECTION / FLEXIBLE ad, impressions and outbound clicks span every rendition
 * while three-second plays fire only on the video one. `hookToClick` (clicks ÷ plays) can therefore
 * exceed 100% and `hookRate` (plays ÷ impressions) is structurally understated — so a band on either
 * would grade the ad's format mix rather than its creative. Hold rate is absent: both its numerator
 * and its denominator are video-scoped whatever the format mix.
 */
const MIXED_RENDITION_KEYS: readonly ICreativeBandKey[] = ['hookRate', 'hookToClick']

/** The rates a purchase count gates, rather than an impression count. */
/**
 * The bands a conversion COUNT gates rather than an impression count.
 *
 * Re-exported from `shared` rather than restated: the grid's cell tones make the same call, and two
 * lists is how the panel and the grid would come to disagree about which bands judge what.
 */
const PURCHASE_DERIVED_KEYS = PURCHASE_CALIBRATED_BAND_KEYS

/** A band that states its range but withholds the verdict, with the reason for withholding it. */
function stated(band: (typeof CREATIVE_METRIC_BANDS)[ICreativeBandKey], reason: string): IBandBadge {
    return {
        badge: band.badge,
        tone: 'neutral',
        reason: `${reason} ${band.grounding}`,
        reading: null,
        grounding: band.grounding,
        withheld: true,
    }
}

/**
 * The badge (and any tone) for one metric's published band, or `null` when no band applies to this
 * creative. `null` value → `null` badge: a rate with no denominator has no band, and coercing it to
 * zero would drop it into the worst edge.
 */
export function evaluateBand(
    key: ICreativeBandKey,
    value: number | null | undefined,
    context: IBandContext,
): IBandBadge | null {
    const band = CREATIVE_METRIC_BANDS[key]
    if (band.scope === 'video' && !context.isVideo) return null

    // A money band outside its own currency can still SAY what it is; it just cannot judge.
    const currencyMismatch = band.currency !== null && band.currency !== context.currency
    const kind = currencyMismatch ? 'reference' : band.kind

    const edge = creativeBandEdge(key, value ?? null)
    if (!edge) {
        // No value to place — the band is still worth stating, so the reader knows what "good" is even
        // on a creative that has no number for it yet.
        return {
            badge: band.badge,
            tone: 'neutral',
            reason: band.grounding,
            reading: null,
            grounding: band.grounding,
            withheld: false,
        }
    }

    // Four gates, each withholding the VERDICT while still stating the band. A stated range is
    // information; a verdict on a number the ad's shape or volume cannot support is a mistake the
    // reader has no way to see.
    if (MIXED_RENDITION_KEYS.includes(key) && !context.isVideoFormat) {
        return stated(
            band,
            'Withheld: this ad serves non-video renditions too, so plays and impressions/clicks count different populations.',
        )
    }
    if (CROSS_SURFACE_STEP_THROUGH_KEYS.includes(key) && (value ?? 0) > 1) {
        return stated(
            band,
            'Withheld: above 100%, so the two counts span different surfaces rather than describing a step-through.',
        )
    }
    if (!bandJudgesOutcome(key, context.isPurchaseOutcome)) {
        return stated(
            band,
            'Withheld: this band is calibrated on purchase-driven returns, and this account\'s outcome is a different event.',
        )
    }
    if (PURCHASE_DERIVED_KEYS.includes(key) && context.purchases < VERDICT_PURCHASES_FLOOR) {
        return stated(
            band,
            `Withheld: ${context.purchases} ${context.purchases === 1 ? 'conversion' : 'conversions'} — too few to judge (needs ${VERDICT_PURCHASES_FLOOR}).`,
        )
    }
    if (band.unit === 'fraction' && !PURCHASE_DERIVED_KEYS.includes(key) && context.impressions < MIN_IMPRESSIONS_FOR_RATE_VERDICT) {
        return stated(
            band,
            `Withheld: ${context.impressions.toLocaleString()} impressions — under ${MIN_IMPRESSIONS_FOR_RATE_VERDICT.toLocaleString()}, the ratio is not stable enough to judge.`,
        )
    }

    const grounding = currencyMismatch
        ? `${band.grounding} This account trades in ${context.currency}, so the band is context rather than a verdict.`
        : band.grounding

    return {
        badge: band.badge,
        tone: kind === 'verdict' ? edge.tone : 'neutral',
        reason: `${band.metricLabel}: ${edge.label}. ${grounding}`,
        reading: edge.label,
        grounding,
        withheld: false,
    }
}

/**
 * The `directional — n = 4 < 5` disclosure, or `null` once the creative clears the floor.
 *
 * Counts the account's TERMINAL conversion event, whatever it is: the floor is a statement about
 * sample size, not about e-commerce, so five trials clear it exactly as five purchases do.
 *
 * The floor lives in `shared` beside the separate, LOWER floor that decides whether a chip may be
 * tinted at all (3 conversions, mirroring the API's `SEGMENT_MIN_CONVERSIONS`). The two are nested,
 * not alternatives: below 3 there is no verdict AND the block is directional; at 3–4 a verdict may
 * show and the block is still directional; from 5 the label goes away.
 */
export function directionalDisclosure(purchases: number): string | null {
    if (purchases >= DIRECTIONAL_PURCHASES_FLOOR) return null
    const n = Number.isInteger(purchases) ? purchases : Math.round(purchases * 10) / 10
    return `directional — n = ${n} < ${DIRECTIONAL_PURCHASES_FLOOR}`
}

/** Whether a longer window is worth suggesting: this window is thin and the creative is judgeable at all. */
export function shouldSuggestWiderWindow(purchases: number, isWidestWindow: boolean): boolean {
    return !isWidestWindow && purchases < DIRECTIONAL_PURCHASES_FLOOR
}
